// errorMessages — shared API-error → operator-facing copy. The gateway
// surfaces backend errors as `{ error: { code, message } }`, where `code`
// is a typed string like BACKOFFICE_000211 / GATEWAY_000023. We render
// friendly single-sentence copy for the codes operators actually hit on
// the login + session paths, and fall back to a status-based message (and
// then the raw server message) for everything else.
import { brokerRefusalMessage } from "@/components/brokers/refusals";
import { APIError } from "./api";

const HUMAN: Record<string, string> = {
  // ----- Auth / session (api-gateway GATEWAY_0000xx) -----
  // These are the codes the gateway actually emits. (The trader app once
  // referenced GATEWAY_000101/102/103, which the gateway never sends — so
  // its raw server message leaked through. The real codes are below.)
  GATEWAY_000003: "Sign in to continue.",
  GATEWAY_000004: "You don't have permission to do that.",
  GATEWAY_000006: "Session invalid. Sign in again.",
  GATEWAY_000007: "Session expired. Sign in again.",
  GATEWAY_000023: "Sign-in failed — check your email and password.",
  // Upstream transport failures (gateway → backoffice).
  GATEWAY_000010: "Backoffice is unreachable. Check the cluster and retry.",
  GATEWAY_000011: "Backoffice is unreachable. Check the cluster and retry.",
  GATEWAY_000012: "Backoffice is unreachable. Check the cluster and retry.",

  // ----- Admin MFA / challenge (backoffice-service BACKOFFICE_0002xx) -----
  // Surface friendly copy instead of the raw server publicMessage
  // ("verification code did not match", "challenge expired", …).
  BACKOFFICE_000202: "This admin account is inactive. Ask a SUPER_ADMIN.",
  BACKOFFICE_000203: "This admin account is locked. Ask a SUPER_ADMIN.",
  BACKOFFICE_000207: "Two-factor verification is required to continue.",
  BACKOFFICE_000208: "Your sign-in session wasn't found. Start the sign-in again.",
  BACKOFFICE_000209: "Your verification window expired. Start the sign-in again.",
  BACKOFFICE_000210: "Too many attempts. Start the sign-in again.",
  BACKOFFICE_000211:
    "That code didn't match. Enter the current code from your authenticator app.",
  BACKOFFICE_000212:
    "That second factor isn't on this account anymore. Ask a SUPER_ADMIN to re-enroll.",
  BACKOFFICE_000213: "That factor isn't active yet. Activate it or pick another.",

  // ----- Admin passwords (SM-534) -----
  // BACKOFFICE_000206 carries a server message naming exactly which
  // rule failed (length, or which classes), so it is deliberately NOT
  // mapped here — the generic copy would be less useful than what the
  // server already wrote.
  BACKOFFICE_000215: "That's not your current password.",
  BACKOFFICE_000216:
    "Set a new password before continuing — the one you have was issued by an administrator.",
  BACKOFFICE_000217: "Pick a password you haven't been using.",

  // ----- Wallet Ops pipeline (SM-254 safe replay / reconcile) -----
  // A reason is enforced client-side, but surface friendly copy in case
  // the server rejects one first.
  BO_REASON_REQUIRED: "Enter a reason — it's recorded in the audit log.",
  // Cursor override is refused unless the scanner is paused first
  // (crypto-wallet-chainnode CWCHAIN_000204).
  CWCHAIN_000204: "Pause this network's scanner before overriding its cursor.",

  // ----- Dispute policy (market-service MARKET_0008xx) -----
  // The policy write is a full replace, so a save built on a stale read
  // would overwrite a concurrent edit wholesale rather than merge with
  // it. The copy has to say the values on screen are no longer the
  // stored ones — "retry" alone would invite re-submitting the same
  // stale payload (SM-324).
  MARKET_000816:
    "Someone else changed this policy while you had it open. Reopen it to see the current values, then reapply your change.",
  MARKET_000813: "Those policy values aren't valid — stake, window, and overdue must each be greater than zero.",

  // ----- System accounts (SM-152 treasury, ledger LEDGER_0007xx) -----
  LEDGER_000700: "That system account doesn't exist.",
  LEDGER_000701: "That account code is already in use — pick another.",
  LEDGER_000702:
    "This operation id was already used for a different account — retry with fresh inputs.",
};

/**
 * Render an error from any admin API call into operator-friendly copy.
 * Falls back to a status-based message (network / server / rate-limit),
 * then to the underlying server message, then to a generic line.
 */
export function humanizeError(err: unknown): string {
  if (err instanceof APIError) {
    const friendly = HUMAN[err.code];
    if (friendly) return friendly;
    // A broker console refusal never shows broker-service's English
    // message: brokerRefusalMessage words it by code, rule or status.
    const broker = brokerRefusalMessage(err);
    if (broker) return broker;
    // Uncoded / unmapped error: lean on the HTTP status so a transport
    // failure doesn't surface as a bare "HTTP 503".
    if (err.status === 0 || err.status >= 500) {
      return "Backoffice is unreachable. Check the cluster and retry.";
    }
    if (err.status === 429) {
      return "Too many attempts. Wait a moment before trying again.";
    }
    if (err.message && !/^HTTP \d+$/.test(err.message)) return err.message;
    return "Something went wrong.";
  }
  if (err instanceof Error && err.message) return err.message;
  return "Something went wrong.";
}

/**
 * MFA-aware variant of humanizeError for the login second-factor step.
 *
 * A wrong second factor always comes back as the same code regardless of
 * the factor kind (BACKOFFICE_000211 "verification code did not match"),
 * so the *method* — known only on the client from the selected factor — is
 * what makes the copy specific. For that "didn't match" case we render
 * per-method copy; everything else (expired challenge, too many attempts,
 * factor not found, …) is method-agnostic and falls through to the shared
 * humanizeError map.
 */
/**
 * Money-move variant for the treasury surface (deposit / withdraw /
 * transfer / fund-user). The ledger reuses its generic wallet codes
 * here, so the copy is contextualised to "source account" instead of
 * leaking raw server messages like "insufficient available credit".
 */
export function humanizeMoneyMoveError(err: unknown): string {
  if (err instanceof APIError) {
    switch (err.code) {
      case "LEDGER_000300":
        return "Not enough available balance in the source account.";
      case "LEDGER_000202":
        return "The source account is frozen — unfreeze it first.";
      case "LEDGER_000203":
        return "That account is closed and can't move money.";
    }
  }
  return humanizeError(err);
}

/**
 * Close-account variant: the zero-balance rejection shares
 * LEDGER_000300 with plain insufficient-funds, so the close flow maps
 * it to close-specific copy instead of "not enough balance".
 */
export function humanizeCloseAccountError(err: unknown): string {
  if (err instanceof APIError && err.code === "LEDGER_000300") {
    return "Balance must be zero before closing — withdraw or transfer the remainder first.";
  }
  return humanizeError(err);
}

export function humanizeMFAError(err: unknown, kind: string): string {
  if (err instanceof APIError && err.code === "BACKOFFICE_000211") {
    switch (kind.toUpperCase()) {
      case "PASSPHRASE":
        return "That passphrase didn't match. Check it and try again.";
      case "BACKUP_CODE":
        return "That backup code didn't match. Try another unused code.";
      case "EMAIL_OTP":
        return "That code didn't match. Enter the latest code we emailed you.";
      case "TOTP":
      default:
        return "That code didn't match. Enter the current code from your authenticator app.";
    }
  }
  return humanizeError(err);
}
